import { ReactElement } from "react";
import moment from "moment";
import clsx from "clsx";

import {
  reviewStatusView,
  statusView,
} from "../../portfolio-agreement/agreement-status";

interface ReviewDetailsProps {
  record: any;
}

const AGREEMENT_TYPES: Record<string, string> = {
  APPENDIX: "Appendix",
  AMENDMENT: "Amendment",
  PORTFOLIO_AGREEMENT: "Portfolio agreement",
};

const date = (value?: string | null): string =>
  value ? moment(value).format("MMM DD, YYYY") : "-";

const text = (value?: string | number | null): string => {
  const shown = value === 0 ? "0" : value;
  return shown ? String(shown) : "-";
};

const Item = ({
  label,
  value,
  wide = false,
}: {
  label: string;
  value: string;
  wide?: boolean;
}): ReactElement => (
  <div className={clsx("item", value === "-" && "is-muted", wide && "is-wide")}>
    <label>{label}</label>
    <p>{value}</p>
  </div>
);

/**
 * Everything the WM needs to decide.
 *
 * Read-only. The one editable value — the applicable date — is rendered by
 * `DateField` beneath this grid, because it behaves differently: it drives the
 * preview and is only persisted on approval.
 */
const ReviewDetails: React.FC<ReviewDetailsProps> = ({
  record,
}): ReactElement => {
  const status = statusView(record?.status);
  const review = reviewStatusView(record?.reviewStatus);

  return (
    <>
      <div className="details">
        <Item label="Portfolio code" value={text(record?.portfolioCode)} />
        <Item
          label="Type"
          value={AGREEMENT_TYPES[record?.type] ?? text(record?.type)}
        />
        <Item label="Status" value={status.label} />
        <Item label="Review status" value={review.label} />

        <Item label="Client (EN)" value={text(record?.clientNameEn)} />
        <Item label="Client (AR)" value={text(record?.clientNameAr)} />
        <Item label="Wealth manager" value={text(record?.wealthManagerName)} />
        <Item label="WM email" value={text(record?.wealthManagerEmail)} />

        <Item label="Current fees" value={text(record?.currentFees)} />
        <Item label="New fees" value={text(record?.newFees)} />

        <Item label="Submitted at" value={date(record?.statusDate)} />
        <Item label="Last updated" value={date(record?.updatedAt)} />
        <Item
          label="Rejection reason"
          value={text(record?.rejectionReason?.description)}
        />
      </div>

      {record?.feedback ? (
        <div className="notice is-warn" style={{ marginTop: 18 }}>
          <p className="title">Previous feedback to the client</p>
          <p>{record.feedback}</p>
        </div>
      ) : null}
    </>
  );
};

export default ReviewDetails;
